// Popover placement geometry for <Explain>.
//
// Pulled out of the component because it's the only part with real logic
// worth testing, and the repo's test environment is `node` — no jsdom, no
// component tests. Everything here is pure arithmetic over rects.
//
// The rules, in order:
//   1. Try the caller's preferred side.
//   2. If the panel doesn't fit there, flip to the opposite side.
//   3. If neither fits, take whichever has more room (the panel will be
//      clamped into the viewport rather than hang off the edge).
//   4. Clamp the cross axis so the panel never leaves the viewport.
//
// This is the same flip/clamp shape CardTile's hover preview already does
// inline (CardTile.tsx) — generalized so every surface gets it for free.

export type ExplainSide = 'top' | 'bottom' | 'left' | 'right';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface PlacementInput {
  /** Trigger bounding box, in viewport coordinates. */
  anchor: Rect;
  /** Measured panel size. */
  panel: Size;
  viewport: Size;
  /** Preferred side. Flipped only when it doesn't fit. */
  side: ExplainSide;
  /** Space between trigger and panel. */
  gap?: number;
  /** Minimum distance from any viewport edge. */
  margin?: number;
}

export interface Placement {
  left: number;
  top: number;
  /** The side actually used, after any flip. Drives the entry animation. */
  side: ExplainSide;
}

export const DEFAULT_GAP = 8;
export const DEFAULT_MARGIN = 8;

const OPPOSITE: Record<ExplainSide, ExplainSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** Space available on `side`, after gap and margin. May be negative. */
export function roomFor(
  side: ExplainSide,
  anchor: Rect,
  viewport: Size,
  gap = DEFAULT_GAP,
  margin = DEFAULT_MARGIN,
): number {
  switch (side) {
    case 'top':
      return anchor.y - gap - margin;
    case 'bottom':
      return viewport.height - margin - (anchor.y + anchor.height + gap);
    case 'left':
      return anchor.x - gap - margin;
    case 'right':
      return viewport.width - margin - (anchor.x + anchor.width + gap);
  }
}

/** Clamp `v` into [min, max], tolerating an inverted range (panel bigger
    than the viewport) by pinning to `min`. */
function clamp(v: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.max(min, Math.min(max, v));
}

/** Resolve which side the panel ends up on. Exported for tests. */
export function resolveSide(
  side: ExplainSide,
  anchor: Rect,
  panel: Size,
  viewport: Size,
  gap = DEFAULT_GAP,
  margin = DEFAULT_MARGIN,
): ExplainSide {
  const needed = side === 'top' || side === 'bottom' ? panel.height : panel.width;
  if (roomFor(side, anchor, viewport, gap, margin) >= needed) return side;

  const flipped = OPPOSITE[side];
  const flippedNeeded =
    flipped === 'top' || flipped === 'bottom' ? panel.height : panel.width;
  if (roomFor(flipped, anchor, viewport, gap, margin) >= flippedNeeded) {
    return flipped;
  }

  // Neither fits — take the roomier one and let the clamp handle the rest.
  return roomFor(flipped, anchor, viewport, gap, margin) >
    roomFor(side, anchor, viewport, gap, margin)
    ? flipped
    : side;
}

export function placePopover({
  anchor,
  panel,
  viewport,
  side,
  gap = DEFAULT_GAP,
  margin = DEFAULT_MARGIN,
}: PlacementInput): Placement {
  const resolved = resolveSide(side, anchor, panel, viewport, gap, margin);

  const centerX = anchor.x + anchor.width / 2 - panel.width / 2;
  const centerY = anchor.y + anchor.height / 2 - panel.height / 2;

  let left: number;
  let top: number;

  switch (resolved) {
    case 'top':
      left = centerX;
      top = anchor.y - gap - panel.height;
      break;
    case 'bottom':
      left = centerX;
      top = anchor.y + anchor.height + gap;
      break;
    case 'left':
      left = anchor.x - gap - panel.width;
      top = centerY;
      break;
    case 'right':
      left = anchor.x + anchor.width + gap;
      top = centerY;
      break;
  }

  return {
    left: clamp(left, margin, viewport.width - panel.width - margin),
    top: clamp(top, margin, viewport.height - panel.height - margin),
    side: resolved,
  };
}
